/**
 * 订单模块跨组件共享的契约常量。
 *
 * 这几份列表是四端（web 用户端 / 后台管理端 / Flutter / 鸿蒙）共用的同一份契约，取值必须
 * 与后端存储的中文字面量逐项一致：后端对 order_status、payment_status、order_category、
 * payment_method 都按中文字面量做匹配或自算（例如订单抵消写入 '订单抵消—订单抵消'、
 * 视角翻转匹配 '收入订单'），并不存在一套英文编码。
 *
 * 改动任意一项都必须同步：
 *   - app_flutter/lib/features/orders/widgets/order_status_utils.dart
 *   - hmos/entry/src/main/ets/pages/OrderFlowCommon.ets
 */

/** 订单执行状态全集，用于筛选与展示；对齐 schema.prisma 的 OrderStatus */
export const ORDER_STATUSES: string[] = ['未执行', '执行中', '已完成', '已取消', '已归档'];

/**
 * 客户端可写入的订单状态白名单（PATCH /orders/:id/status）。
 *
 * 未执行 / 执行中 / 已完成 由后端依据订单起止时间自算（deriveDisplayStatus），
 * 直接下发会被 400 拒绝，所以「可写集合」不等于「状态全集」。
 */
export const ORDER_STATUSES_WRITABLE: string[] = ['已取消', '已归档'];

/**
 * 支付状态筛选值域，逐字对齐后端 `orders.payment_status`（PG 枚举 PaymentStatus）。
 *
 * 不含「已退款」：订单的付款状态由后端按金额自算，只有未付款 / 部分付款 / 已结清三值，
 * 退款是会员订单与支付流水上的独立语义，从来不会写进这一列。把它列成筛选项等于给了一个
 * 永远筛不出东西、且现在会被后端判成 400 的按钮。展示侧对历史英文值的兼容映射不受影响。
 */
export const PAYMENT_STATUSES: string[] = ['未付款', '部分付款', '已结清'];

/**
 * 付款状态筛选器的展示文案。值仍是 `payment_status` 枚举本身，只有「未付款」这句话要说清口径：
 * 服务端给未付款筛选附加了 `order_amount > 0`，0 元单（先占档期后定价）不在其中。
 */
export const PAYMENT_STATUS_FILTER_LABELS: Record<string, string> = { 未付款: '未付款（有欠款）' };

/** 支付方式契约值：写入 order.payment_method 的就是这些中文本身，没有 value/label 双轨 */
export const PAYMENT_METHODS: string[] = ['现金', '微信支付', '支付宝', '银行转账', '对公转账', '其他'];

/**
 * 对接电话的格式闸门：这一格填的是「活动现场那个人的手机号」，所以按 11 位手机号判，
 * 与注册/备用电话用的是同一条式子（全站内联了六份，此处是订单域的这一份）。
 *
 * 空值放行——对接电话是选填项，挡的是「填错了」不是「没填」。
 * 同步镜像：`app_flutter/lib/features/orders/utils/order_form_rules.dart`、
 * `hmos/entry/src/main/ets/pages/OrderFlowCommon.ets`、后端 `create-order.dto.ts` 的 `@Matches`。
 */
export const CONTACT_PHONE_RE = /^1[3-9]\d{9}$/;

/** 对接电话不合法时返回那句提示，合法或留空返回空串 */
export function contactPhoneFormatError(value: string | null | undefined): string {
  const v = (value ?? '').trim();
  return v === '' || CONTACT_PHONE_RE.test(v) ? '' : '对接电话格式不正确（须为 11 位手机号）';
}
